/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useEmployeeService, useEmployeeUrl, useEmployeeNamesService, EmployeeLink } from 'domain/shared/employee';
import { useShowContext } from 'framework/show';
import { ReplaceUrlWithLink } from 'framework/utils';
import { HtmlField } from 'framework/ui/field';
import { Button } from 'framework/ui/mui-builder';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';
import AddIcon from '@mui/icons-material/Add';
import { FormFieldsData } from 'framework/form';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import { TimelineItem } from './types';
import { HeaderContainer, NoteContent, TimelineContainer } from './styles';
import LightTimelineNoteForm from './LightTimelineNoteForm';
import timelineUtils from './utils';

const LightTimeline: React.FC = () => {
  const { data } = useShowContext();
  const { searchTimelineItems, createTimelineItem, getFullName } = useEmployeeService();
  const { getEmployeeNamesByAliases } = useEmployeeNamesService();
  const { getTimelineUrl } = useEmployeeUrl();
  const { addMessage } = useMessageService();
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [employeeNames, setEmployeeNames] = useState<Record<string, any>>({});
  const [isAddingNote, setIsAddingNote] = useState(false);

  const fetchTimelineItems = useCallback(async () => {
    if (!data.employeeAlias) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Set filter to only get notes and limit to 2 items
      const res = await searchTimelineItems(data.employeeAlias, {
        filter: {
          type: {
            in: ['NOTE_CREATED'],
          },
        },
        pageNumber: 0,
        pageSize: 2,
        sort: {
          createdAt: 'DESC',
        },
      });

      const timelineItems = (res.result || []) as unknown as TimelineItem[];
      setItems(timelineItems);

      await fetchEmployeeNames(timelineItems);
    } catch (error) {
      addMessage(new ErrorMessage('Failed to fetch timeline items.'));
    } finally {
      setLoading(false);
    }
  }, [data.employeeAlias]);

  const fetchEmployeeNames = async (timelineItems: TimelineItem[]) => {
    const employeeAliases = timelineItems.map(item => item.author || item.employee).filter(Boolean);

    if (employeeAliases.length > 0) {
      try {
        const namesData = await getEmployeeNamesByAliases(employeeAliases);

        const namesRecord: Record<string, any> = {};
        namesData.forEach((nameData: any) => {
          if (nameData && nameData.alias) {
            namesRecord[nameData.alias] = nameData;
          }
        });

        setEmployeeNames(namesRecord);
      } catch (error) {
        addMessage(new ErrorMessage('Failed to fetch employee names'));
      }
    }
  };

  useEffect(() => {
    fetchTimelineItems();
  }, [fetchTimelineItems]);

  const handleAddNote = () => {
    setIsAddingNote(true);
  };

  const handleCancelAddNote = () => {
    setIsAddingNote(false);
  };

  const handleSubmitNote = useCallback(
    async (values: FormFieldsData): Promise<object | void> => {
      if (!data.employeeAlias) {
        addMessage(new ErrorMessage('Worker ID is missing'));
        return {};
      }

      try {
        await createTimelineItem(data.employeeAlias, 'notes', values);
        addMessage(new SuccessMessage('Note has been added successfully'));
        setIsAddingNote(false);

        await fetchTimelineItems();
        return {};
      } catch (error) {
        addMessage(new ErrorMessage('Failed to add note'));
        return {};
      }
    },
    [data.employeeAlias],
  );

  const getAuthorName = (item: TimelineItem): string => {
    return timelineUtils.getAuthorName(item, employeeNames, getFullName);
  };

  const renderNoteForm = () => (
    <Box mb={2}>
      <LightTimelineNoteForm
        onCancel={handleCancelAddNote}
        submitFormFunc={handleSubmitNote}
        afterSubmit={{
          message: 'Note has been added successfully',
        }}
      />
    </Box>
  );

  const renderEmptyState = () => (
    <Typography variant="body1" color="textSecondary">
      No notes available in this employee timeline yet.
    </Typography>
  );

  const renderNotesList = () => (
    <>
      <Box>
        {items.map(item => {
          const timelineItemEntity = item.note;

          if (!timelineItemEntity) return null;

          return (
            <NoteContent key={`note-${timelineItemEntity.id}`} elevation={2}>
              <Typography variant="body2" color="textSecondary" display="block" mb={0.5}>
                {new Date(item.createdAt).toLocaleDateString()} by{' '}
                <EmployeeLink id={item.employee} name={getAuthorName(item)} isOpenInNewTab />
              </Typography>
              <ReplaceUrlWithLink>
                <HtmlField value={timelineItemEntity.noteText} />
              </ReplaceUrlWithLink>
              {timelineItemEntity.attachmentsIds && timelineItemEntity.attachmentsIds.length > 0 && (
                <Box mt={1}>
                  <AttachmentsListField value={timelineItemEntity.attachmentsIds} />
                </Box>
              )}
            </NoteContent>
          );
        })}
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={1}>
        <Link to={getTimelineUrl(data.employeeAlias)} target="_blank" style={{ fontSize: '0.8rem' }}>
          View all Notes
        </Link>
      </Box>
    </>
  );

  const renderContent = useCallback(() => {
    if (isAddingNote) {
      return renderNoteForm();
    }

    return items.length === 0 ? renderEmptyState() : renderNotesList();
  }, [isAddingNote, items.length]);

  if (loading) {
    return (
      <TimelineContainer>
        <Typography variant="h4" color="primary" gutterBottom borderBottom="1px solid" borderColor="border.strong">
          Last timeline notes
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" height="100px">
          <Typography variant="body1">Loading...</Typography>
        </Box>
      </TimelineContainer>
    );
  }

  return (
    <TimelineContainer>
      <HeaderContainer borderBottom="1px solid var(--mui-palette-border-strong)">
        <Typography variant="h4" color="primary">
          Last timeline notes
        </Typography>
        <Button
          variant="contained"
          sx={{
            width: '14px',
            minWidth: '14px',
            height: '14px',
            padding: '0',
            '> span': {
              width: '5px',
              '> svg.MuiSvgIcon-root': {
                fontSize: '16px',
              },
            },
          }}
          onClick={handleAddNote}
          startIcon={<AddIcon />}
          disabled={isAddingNote}
        />
      </HeaderContainer>
      {renderContent()}
    </TimelineContainer>
  );
};

export default LightTimeline;
